import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight, Check, Zap, FileText, Tag, ShieldCheck } from 'lucide-react'
import { DARK, TEAL, BG, MarketingNavBar, MarketingFooter, FaqAccordion, faqJsonLd, KeywordCrossLinks } from '../../components/MarketingLayout'

const FAQS = [
  {
    q: 'Mi az a gyártmánylap, és miért kötelező?',
    a: 'A gyártmánylap az élelmiszer-előállítás hatósági és minőségügyi dokumentuma: tartalmazza a receptúrát, a felhasznált alapanyagokat, az allergéneket, a tápértéket, az eltarthatóságot és a tárolási feltételeket. Cukrászdák, pékségek és vendéglátóhelyek számára a NÉBIH és a fogyasztóvédelem is kérheti ellenőrzéskor.',
  },
  {
    q: 'Mennyi idő alatt készül el egy gyártmánylap a CukrászDoc-kal?',
    a: 'Ha a recept alapanyagai már szerepelnek az adatbázisban (400+ alapanyag beépítve), a gyártmánylap 1-2 perc alatt elkészül: beírod a receptet, a rendszer kiszámolja a tápértéket és az allergéneket, te pedig letöltöd PDF-ben.',
  },
  {
    q: 'Milyen adatokat tartalmaz az elkészült gyártmánylap PDF?',
    a: 'Terméknév, receptúra és összetevők, allergénjelölés, tápértéktáblázat (EU 1169/2011 szerint), eltarthatóság, tárolási feltételek és a kiállítás dátuma — nyomtatásra kész, hatósági ellenőrzésre alkalmas formában.',
  },
  {
    q: 'Kézzel kell begépelnem a tápértékeket?',
    a: 'Nem. Csak az alapanyagot és a mennyiséget adod meg, a kalória, fehérje, zsír, szénhidrát és allergén adatokat a rendszer automatikusan kiszámolja és beilleszti a gyártmánylapba.',
  },
  {
    q: 'Ingyenesen kipróbálható a gyártmánylap-készítő?',
    a: 'Igen, regisztráció után 3 receptig ingyenesen elkészítheted a gyártmánylapokat, kártyaszám megadása nélkül.',
  },
]

export default function GyartmanylapPage() {
  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Helmet>
        <title>Gyártmánylap készítés online, gyorsan — CukrászDoc</title>
        <meta name="description" content="Készíts gyártmánylapot receptből 1-2 perc alatt. Automatikus tápérték- és allergénszámítás, nyomtatásra kész PDF. Próbáld ki ingyen." />
        <link rel="canonical" href="https://cukraszdoc.hu/gyartmanylap" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd(FAQS))}</script>
      </Helmet>
      <MarketingNavBar />

      <section style={{ background: `linear-gradient(160deg, ${DARK} 0%, #162119 100%)`, paddingTop: '5rem' }} className="relative overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-6 py-16 lg:py-24">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.3)' }}>
            <Zap size={12} />
            Gyártmánylap gyorsan, kézi számolgatás nélkül
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight text-white mb-5">
            Gyártmánylap készítés <span style={{ color: TEAL }}>online, percek alatt</span>
          </h1>
          <p className="text-lg sm:text-xl max-w-2xl leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Írd be a receptet, a CukrászDoc automatikusan kiszámolja a tápértéket és az allergéneket, majd elkészíti a hatósági ellenőrzésre kész, nyomtatható gyártmánylapot — kézi Excel-táblák és utánaszámolás nélkül.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-base transition-all" style={{ background: TEAL, color: '#fff' }}>
              Készítsd el az első gyártmánylapod ingyen <ArrowRight size={16} />
            </Link>
          </div>
        </div>
        <div className="relative" style={{ height: 60, marginBottom: -2 }}>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="absolute bottom-0 w-full h-full">
            <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#ffffff" />
          </svg>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-6" style={{ color: DARK }}>Mi az a gyártmánylap?</h2>
          <p className="text-base leading-relaxed text-gray-600 mb-4">
            A gyártmánylap (más néven receptlap vagy termékleírás) minden saját készítésű élelmiszertermék kötelező kísérődokumentuma. Tartalmazza a receptúrát, az összetevőket, az allergénjelölést, a tápértéktáblázatot és az eltarthatósági, tárolási előírásokat — az EU 1169/2011 rendelet és a magyar élelmiszer-biztonsági szabályok szerint.
          </p>
          <p className="text-base leading-relaxed text-gray-600">
            Cukrászdában, pékségben vagy catering vállalkozásban minden egyes termékhez külön gyártmánylap kell — ez kézzel, Excelben rendkívül időigényes. A CukrászDoc ezt a folyamatot automatizálja: a recept beírása után a gyártmánylap egy kattintással, nyomtatásra kész PDF-ként letölthető.
          </p>
          <p className="text-base leading-relaxed text-gray-600 mt-4">
            Ha most indulsz, és nem csak a gyártmánylapra vagy kíváncsi, összeszedtük a teljes dokumentációs listát is: <Link to="/blog/cukraszda-nyitas-dokumentacio" style={{ color: TEAL, fontWeight: 600 }}>cukrászda nyitás dokumentációja — checklist</Link>.
          </p>
        </div>
      </section>

      <section style={{ background: BG }} className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: DARK }}>Mit tartalmaz az automatikusan elkészült gyártmánylap?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: FileText, title: 'Receptúra & összetevők', desc: 'Alapanyagok, mennyiségek, technológiai leírás.' },
              { icon: ShieldCheck, title: 'Allergénjelölés', desc: 'Automatikusan kigyűjtve az alapanyag-adatbázisból.' },
              { icon: Zap, title: 'Tápértéktáblázat', desc: 'Kalória, fehérje, zsír, szénhidrát — 100g-ra és adagra.' },
              { icon: Tag, title: 'Eltarthatóság & tárolás', desc: 'Kötelező jelölések, automatikusan kitöltve.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-6" style={{ border: '1px solid rgba(91,184,160,0.15)' }}>
                <Icon size={22} style={{ color: TEAL }} className="mb-3" />
                <h3 className="font-semibold mb-2 text-base" style={{ color: DARK }}>{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-4" style={{ color: DARK }}>Gyártmánylap 3 lépésben</h2>
          <div className="flex flex-col gap-4 mt-10">
            {[
              'Beírod a recept alapanyagait és mennyiségeit.',
              'A rendszer automatikusan kiszámolja a tápértéket, az allergéneket és a kötelező jelöléseket.',
              'Letöltöd a kész gyártmánylapot PDF-ben, és kinyomtatod.',
            ].map((step, i) => (
              <div key={step} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-sm" style={{ background: DARK, color: TEAL }}>{i + 1}</div>
                <p className="text-base text-gray-600 pt-1">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ background: BG }} className="py-20">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-10" style={{ color: DARK }}>Gyakori kérdések a gyártmánylapról</h2>
          <FaqAccordion items={FAQS} />
        </div>
      </section>

      <KeywordCrossLinks current="/gyartmanylap" />

      <section style={{ background: DARK }} className="py-20">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Próbáld ki — az első gyártmánylapod ingyen elkészül</h2>
          <p className="mb-8 text-base" style={{ color: 'rgba(255,255,255,0.55)' }}>Regisztráció kártyaszám nélkül. 3 recept erejéig teljesen ingyenes.</p>
          <Link to="/register" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base" style={{ background: TEAL, color: '#fff' }}>
            <Check size={17} /> Ingyenes regisztráció
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
